import { Injectable } from '@angular/core';
import {
  Agreements,
  Invoices,
  Invoices_Insert_Input,
  ShipmentFulfillments,
  ShipmentFulfillmentsStatus,
} from '@etoh/database/core';
import { keys, sum } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { combineLatest, of, switchMap, take } from 'rxjs';
import { StoreService } from '../store.service';

@Injectable({
  providedIn: 'root',
})
export class ShipmentFullmentsService {
  constructor(
    private storeService: StoreService,
    private message: NzMessageService
  ) {}

  public createInvoices(shipmentFulfillments: ShipmentFulfillments) {
    return this.storeService
      .getObjectFromStoreById<Agreements>(
        'agreements',
        shipmentFulfillments.agreementId
      )
      .pipe(
        take(1),
        switchMap((aggrement) => {
          if (!shipmentFulfillments.commissionAmount) {
            this.message.warning(
              `No invoices to generate,  missing commission amount: ${shipmentFulfillments.id}`
            );
            return of(null);
          }

          const commissionParties = {
            seller: {
              commission: aggrement.commissionSeller,
              company: aggrement.sellerCompanyId,
            },
            buyer: {
              commission: aggrement.commissionBuyer,
              company: aggrement.buyerCompanyId,
            },
            thirdParty: {
              commission: aggrement.commissionThirdParty,
              company: aggrement.thirdPartyCompanyId,
            },
          };

          const invoicesToCreate: (Invoices_Insert_Input | undefined)[] = keys(
            commissionParties
          )
            .map((party) => {
              const p = (commissionParties as any)[party];

              if (!p.commission || !p.company) {
                return;
              }

              let commissionPrice: number | undefined;
              if (aggrement.commissionType === 'flat') {
                commissionPrice = aggrement.commissionFlatFees as number;
              } else if (aggrement.commissionType === '%') {
                commissionPrice =
                  (shipmentFulfillments.commissionAmount as number) *
                  (p.commission / 100);
              } else if (aggrement.commissionType === 'per UOM') {
                commissionPrice =
                  (shipmentFulfillments.commissionAmount as number) *
                  (p.commission / 100);
              }

              if (!commissionPrice) {
                return;
              }

              return {
                agreementId: shipmentFulfillments.agreementId,
                amount: commissionPrice * (p.commission / 100),
                currency: aggrement.commissionCurrency,
                status: 'invoiced',
                shipmentFulfillmentId: shipmentFulfillments.id,
                officeId: aggrement.contractOfficeId,
                brokerId: aggrement.contractUserId,
                companyId: p.company,
              };
            })
            .filter((i) => !!i);

          if (!invoicesToCreate?.length) {
            this.message.warning(
              `No invoices to generate, probably missing informations: ${shipmentFulfillments.id}`
            );
            return of(null);
          }

          return combineLatest([
            this.storeService.updateObjectByPk<ShipmentFulfillments>(
              'shipmentFulfillments',
              shipmentFulfillments.id,
              {
                status: 'invoiced',
              }
            ),
            ...invoicesToCreate.map((i: any) =>
              this.storeService.addObject<Invoices>('invoices', i)
            ),
          ]);
        })
      );
  }

  public getStatus = (
    type: 'ordered' | 'shipped' | 'invoiced'
  ): ShipmentFulfillmentsStatus[] => {
    if (type === 'ordered') {
      return ['ordered', 'fulfilled', 'invoiced'];
    } else if (type === 'shipped') {
      return ['fulfilled', 'invoiced'];
    } else if (type === 'invoiced') {
      return ['invoiced'];
    }

    return [];
  };

  public getFulfillments = (
    shipmentsFullments: ShipmentFulfillments[],
    status: ShipmentFulfillmentsStatus[]
  ) => {
    return shipmentsFullments.filter((i) =>
      i.status ? status.includes(i.status) : false
    );
  };

  public getFulfillmentsQuantity = (
    shipmentsFullments: ShipmentFulfillments[],
    status: ShipmentFulfillmentsStatus[]
  ): number => {
    const shipmentsFullmentsFiltered = this.getFulfillments(
      shipmentsFullments,
      status
    );

    const sumQuantity =
      sum(shipmentsFullmentsFiltered.map((i) => i.quantity || 0)) || 0;

    return sumQuantity;
  };
}
